import { ReactNode, useState, useEffect} from 'react';
import { useSwipeable } from 'react-swipeable';
import { FaChevronLeft, FaChevronRight } from 'react-icons/fa';

interface CarouselProps<T> {
  items: T[];
  renderItem: (item: T, isCenter: boolean) => ReactNode;
  wrapperClassName?: string;
}

function Carousel<T>({ items, renderItem, wrapperClassName = '' }: CarouselProps<T>) {
  const [visibleCount, setVisibleCount] = useState(3);
  const totalItems = items.length;
  const [current, setCurrent] = useState(visibleCount);
  const [isTransitioning, setIsTransitioning] = useState(true);

  // Responsive logic
  useEffect(() => {
    const handleResize = () => {
      setVisibleCount(window.innerWidth < 960 ? 1 : 3);
    };
    handleResize(); // initial check
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Update current when visibleCount changes
  useEffect(() => {
    setCurrent(visibleCount);
  }, [visibleCount]);

  const clonedStart = items.slice(-visibleCount);
  const clonedEnd = items.slice(0, visibleCount);
  const allItems = [...clonedStart, ...items, ...clonedEnd];
  const totalSlides = allItems.length;

  const prev = () => setCurrent((c) => c - 1);
  const next = () => setCurrent((c) => c + 1);

  const handlers = useSwipeable({
    onSwipedLeft: () => next(),
    onSwipedRight: () => prev(),
    trackMouse: true,
  });

  useEffect(() => {
    if (current === 0) {
      setTimeout(() => {
        setIsTransitioning(false);
        setCurrent(totalItems);
      }, 500);
    } else if (current === totalItems + visibleCount) {
      setTimeout(() => {
        setIsTransitioning(false);
        setCurrent(visibleCount);
      }, 500);
    }
  }, [current]);

  useEffect(() => {
    if (!isTransitioning) {
      requestAnimationFrame(() => {
        setIsTransitioning(true);
      });
    }
  }, [isTransitioning]);

  const getTransform = () => `translateX(-${(current * 100) / totalSlides}%)`;

  return (
    <div className={`relative overflow-hidden w-full ${wrapperClassName}`}>
      {/* Prev Button */}
      <button
        onClick={prev}
        className="absolute hover:cursor-pointer left-4 top-45 -translate-y-1/2 z-20 bg-white rounded-full p-2 shadow hover:bg-gray-100"
      >
        <FaChevronLeft size={20} className="text-gray-700" />
      </button>

      {/* Carousel */}
      <div {...handlers} className="w-full pb-10 overflow-hidden">
        <div
          className="flex"
          onTransitionEnd={() => {
            if (current === 0 || current === totalItems + visibleCount) {
              // no-op, handled above
            }
          }}
          style={{
            transform: getTransform(),
            transition: isTransitioning ? 'transform 0.5s ease-in-out' : 'none',
            width: `${(totalSlides / visibleCount) * 100}%`,
          }}
        >
          {allItems.map((item, index) => {
            const isCenter = index === current + Math.floor(visibleCount / 2);
            return (
              <div
                key={index}
                className="flex-shrink-0 flex justify-center items-center"
                style={{
                  width: `${100 / totalSlides}%`,
                  minHeight: '200px',
                }}
              >
                <div className={isCenter ? 'scale-100 opacity-100' : 'scale-95 opacity-80'}>
                  {renderItem(item, isCenter)}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Next Button */}
      <button
        onClick={next}
        className="absolute hover:cursor-pointer right-4 top-45 -translate-y-1/2 z-20 bg-white rounded-full p-2 shadow hover:bg-gray-100"
      >
        <FaChevronRight size={20} className="text-gray-700" />
      </button>

      {/* Dots */}
      <div className="flex justify-center space-x-2 mt-6">
        {items.map((_, i) => (
          <button
            key={i}
            onClick={() => setCurrent(i + visibleCount)}
            className={`text-2xl transition-colors duration-300 ${
              (current - visibleCount + totalItems) % totalItems === i
                ? 'text-gray-900'
                : 'text-gray-400'
            }`}
          >
            •
          </button>
        ))}
      </div>
    </div>
  );
}

export default Carousel;
